import axios from "axios";

// UserTripJsonController base URL
const BASE_URL = "/lmiga/user/trip";

/**
 * Create a new saved route with alert preferences.
 * Requires alertsLoggedIn cookie (set by TripLoginJsonController on login).
 *
 * @param {{ displayName: string, waypoints: Array<{lat,lng}>, preferences: object, routeData?: object }} data
 */
export const createRoute = async ({
  displayName,
  waypoints,
  preferences,
  routeData,
}) => {
  try {
    const response = await axios.post(`${BASE_URL}/create.json`, {
      displayName,
      waypoints,
      preferences,
      routeData,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating route:", error);
    throw error.response?.data || { message: "Failed to create route" };
  }
};

/**
 * List all saved routes for the authenticated user.
 */
export const getAllRoutes = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/routes.json`);
    return response.data;
  } catch (error) {
    console.error("Error fetching routes:", error);
    throw error.response?.data || { message: "Failed to fetch routes" };
  }
};

/**
 * Get a saved route with its full alert configuration (SavedRouteDto).
 */
export const getRoute = async (routeId) => {
  try {
    const response = await axios.get(`${BASE_URL}/${routeId}/route.json`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching route ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to fetch route" };
  }
};

/**
 * Update a saved route's name, preferences, and/or geometry.
 */
export const updateRoute = async (
  routeId,
  { displayName, preferences, waypoints, routeData },
) => {
  try {
    const response = await axios.put(`${BASE_URL}/${routeId}/route.json`, {
      displayName,
      preferences,
      waypoints,
      routeData,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating route ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to update route" };
  }
};

/**
 * Delete a saved route.
 */
export const deleteRoute = async (routeId) => {
  try {
    const response = await axios.delete(`${BASE_URL}/${routeId}/route.json`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting route ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to delete route" };
  }
};

/**
 * Get a real-time traffic + events report for a saved route.
 */
/**
 * Get cameras along a route path.
 * @param {Array<{lat: number, lng: number}>} path - Route path coordinates
 * @param {number[]} incidentDistances - Distance along route for each incident (meters)
 * @param {number|null} congestionDistance - Distance along route of worst congestion (meters)
 * @returns {Promise} GeoJSON FeatureCollection
 */
export const getRouteCameras = async (
  path,
  incidentDistances,
  congestionDistance,
) => {
  try {
    const response = await axios.post("/lmiga/tripRouteCameras.json", {
      path,
      incidentDistances: incidentDistances || [],
      congestionDistance: congestionDistance ?? null,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching route cameras:", error);
    throw error.response?.data || { message: "Failed to fetch route cameras" };
  }
};

/**
 * Send a one-off alert email immediately for a saved route.
 * @param {number} routeId - The saved route ID
 * @param {object} [preferences] - Optional unsaved preferences to use
 */
export const sendNowEmail = async (routeId, preferences) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/${routeId}/send-now.json`,
      preferences || {},
    );
    return response.data;
  } catch (error) {
    console.error(`Error sending test email for route ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to send test email" };
  }
};

/**
 * Get stored route map data (path + routeData) for a saved route — no re-planning.
 */
export const getRouteMap = async (routeId) => {
  try {
    const response = await axios.get(`${BASE_URL}/${routeId}/routeMap.json`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching route map for ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to fetch route map" };
  }
};

/**
 * Get live traffic data for a saved route.
 */
export const getRouteTraffic = async (routeId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/${routeId}/routeTraffic.json`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching route traffic for ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to fetch route traffic" };
  }
};

/**
 * Get live traffic data for a route using routeData (public, no auth).
 */
export const getPublicRouteTraffic = async (routeData) => {
  try {
    const response = await axios.post("/lmiga/routeTraffic.json", {
      routeData,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching public route traffic:", error);
    throw error.response?.data || { message: "Failed to fetch route traffic" };
  }
};

/**
 * Get weather alerts and winter conditions for a saved route.
 */
export const getRouteWeather = async (routeId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/${routeId}/routeWeather.json`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching route weather for ${routeId}:`, error);
    throw error.response?.data || { message: "Failed to fetch route weather" };
  }
};

/**
 * Get weather alerts and winter conditions for a route using routeData (public, no auth).
 */
export const getPublicRouteWeather = async (routeData) => {
  try {
    const response = await axios.post("/lmiga/routeWeather.json", {
      routeData,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching public route weather:", error);
    throw error.response?.data || { message: "Failed to fetch route weather" };
  }
};
